import { lazy, type ComponentType } from "react";

/**
 * Pages and dialogs that aren't needed for the first screen are loaded on demand (code splitting). The catch: after a
 * deploy, an open page can ask for a chunk file that no longer exists, and this host answers a missing file with the
 * app's HTML (the failure that blanked the app on 2026-10-06). The fix is to reload once, which fetches the new version;
 * a timestamp in sessionStorage makes sure that can never turn into a reload loop.
 */
const KEY = "ptv:stale-chunk-reload";
export const RELOAD_GUARD_MS = 30_000;

/** True when a reload is worth trying: none was attempted in the last RELOAD_GUARD_MS (a clock that went backwards counts as none). */
export function shouldReloadForStaleChunk(lastAttempt: number | null, now: number): boolean {
  if (lastAttempt === null) return true;
  return now < lastAttempt || now - lastAttempt >= RELOAD_GUARD_MS;
}

/** Reload the page once to pick up the new version. Returns false (and does nothing) if one was just tried. */
export function reloadOnceForStaleChunk(): boolean {
  let last: number | null = null;
  try {
    const raw = sessionStorage.getItem(KEY);
    const n = raw === null ? NaN : Number(raw);
    last = Number.isFinite(n) ? n : null;
  } catch {
    /* storage blocked: still allow one reload; the guard just can't remember it */
  }
  const now = Date.now();
  if (!shouldReloadForStaleChunk(last, now)) return false;
  try {
    sessionStorage.setItem(KEY, String(now));
  } catch {
    /* ignore */
  }
  window.location.reload();
  return true;
}

/**
 * `React.lazy` for a named export, with the stale-chunk guard. If loading fails and a reload is under way, the promise
 * never settles (the page is about to be replaced); otherwise the error reaches the nearest ChunkErrorBoundary.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyNamed<K extends string, M extends { [P in K]: ComponentType<any> }>(load: () => Promise<M>, name: K) {
  return lazy(() =>
    load().then(
      (m) => ({ default: m[name] }),
      (err: unknown) => {
        if (reloadOnceForStaleChunk()) return new Promise<never>(() => undefined);
        throw err;
      },
    ),
  );
}

/** Start fetching chunks the player is likely to want, once the browser is idle, so opening them later is instant. */
export function prefetchWhenIdle(loaders: ReadonlyArray<() => Promise<unknown>>): void {
  const run = () => loaders.forEach((l) => void l().catch(() => undefined));
  const w = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number };
  if (w.requestIdleCallback) w.requestIdleCallback(run, { timeout: 4000 });
  else setTimeout(run, 2000);
}
